import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Check, DownloadSimple } from "@phosphor-icons/react";
import {
  JsonResume,
  JsonResumeParser,
  LinkedIn,
  LinkedInParser,
  ReactiveResumeParser,
  ReactiveResumeV3,
  ReactiveResumeV3Parser,
} from "@reactive-resume/parser";
import { ResumeData } from "@reactive-resume/schema";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@reactive-resume/ui";
import { AuthResponseDto, LoginDto, } from "@reactive-resume/dto";

import { AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z, ZodError } from "zod";
import { axios } from "@/client/libs/axios";
import { AxiosError, AxiosResponse } from "axios";

import { useToast } from "@/client/hooks/use-toast";
import { setCoachMetadata } from "@/client/libs/coach-metadata";
import { useImportResume } from "@/client/services/resume/import";
import { useDialog } from "@/client/stores/dialog";

enum ImportType {
  "reactive-resume-json" = "reactive-resume-json",
  "reactive-resume-v3-json" = "reactive-resume-v3-json",
  "json-resume-json" = "json-resume-json",
  "linkedin-data-export-zip" = "linkedin-data-export-zip",
  "linkedin-data-export-website" = "linkedin-data-export-website",
  "lpu-resume-json" = "lpu-resume-json"
}

const formSchema = z.object({
  file: z.instanceof(File),
  type: z.nativeEnum(ImportType),
  url:  z.string()
});

type FormValues = z.infer<typeof formSchema>;

type ValidationResult =
  | {
      isValid: false;
      errors: string;
    }
  | {
      isValid: true;
      type: ImportType;
      result: ResumeData | ReactiveResumeV3 | LinkedIn | JsonResume;
    };


export const ImportDialog = () => {
  const { toast } = useToast();
  const { isOpen, close } = useDialog("import");
  const { importResume, loading, error: importError } = useImportResume();

  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [lodr, setLodr] = useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });
  const filetype = form.watch("type");

  useEffect(() => {
    if (isOpen) onReset();
  }, [isOpen]);

  useEffect(() => {
    form.reset({ file: undefined, type: filetype });
    setValidationResult(null);
  }, [filetype]);

  const accept = useMemo(() => {
    if (!filetype) return "";
    if (filetype.includes("json")) return ".json";
    if (filetype.includes("zip")) return ".zip";
    return "";
  }, [filetype]);

  const onValidate = async () => {
    setLodr(true);
    const { file, type, url } = form.getValues();

    try {
      if (type === ImportType["lpu-resume-json"]) {
        // get cookies for refresh token or authorization
        const cookies = document.cookie.split(';');
        let refreshToken = '';
        cookies.forEach((cookie) => {
          if (cookie.includes('refreshToken') || cookie.includes('Authorization')) {
            refreshToken = cookie.split('=')[1];
          }
        });

        const response: any = await axios.post<AuthResponseDto, AxiosResponse<AuthResponseDto>>(
          "/auth/importLpuUserData", { refreshToken }
        );

        const organizationName = response.data.data?.metadata?.organizationName;
        const programName = response.data.data?.metadata?.programName;

        setCoachMetadata(organizationName, programName);

        const parser = new ReactiveResumeParser();
        const result = await parser.validate(response.data.data);
        setLodr(false);
        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["reactive-resume-json"]) {
        // get cookies for refresh token or authorization
        const cookies = document.cookie.split(';');
        let refreshToken = '';
        cookies.forEach((cookie) => {
          if (cookie.includes('refreshToken')|| cookie.includes('Authorization') ) {
            refreshToken = cookie.split('=')[1];
          }
        });

        const response:any =  await axios.post<AuthResponseDto, AxiosResponse<AuthResponseDto>>(
          "/auth/importUserData",{refreshToken}
        );

        console.log(response.data.data);
        console.log('reactive-resume-jsonsc');
        const parser = new ReactiveResumeParser();
        // const data = await parser.readFile(file);

        const result = await parser.validate(response.data.data);
        setLodr(false);
        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["reactive-resume-v3-json"]) {
        const parser = new ReactiveResumeV3Parser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);
        setLodr(false);
        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["json-resume-json"]) {
        const parser = new JsonResumeParser();
        const data = await parser.readFile(file);
        const result = parser.validate(data);
        setLodr(false);
        setValidationResult({ isValid: true, type, result });
      }

      if (type === ImportType["linkedin-data-export-zip"]) {
        const parser = new LinkedInParser();
        const data = await parser.readFile(file);
        const result = await parser.validate(data);
        setLodr(false);
        setValidationResult({ isValid: true, type, result });
      }
    } catch (error: any) {
      setLodr(false);
      if (error instanceof ZodError) {
        setValidationResult({
          isValid: false,
          errors: error.toString(),
        });

        toast({
          variant: "error",
          title: t`An error occurred while validating the file.`,
        });
      } else if (error instanceof AxiosError) {
        toast({
          variant: "error",
          // eslint-disable-next-line lingui/no-unlocalized-strings
          title: "Invalid Linkedin public URL"
        })
      } else {
        toast({
          variant: "error",
          title: `${error.message}`
        })
      }
    }
  };

  const onImport = async () => {
    const { type } = (filetype=='reactive-resume-json' || filetype === 'lpu-resume-json' || filetype === 'linkedin-data-export-website') ? form.getValues():formSchema.parse(form.getValues());

    if (!validationResult?.isValid || validationResult.type !== type) return;

    try {
      if (type === ImportType["reactive-resume-json"]) {
        const parser = new ReactiveResumeParser();
        const data = parser.convert(validationResult.result as ResumeData);

        await importResume({ data });
      }

      if (type === ImportType["lpu-resume-json"]) {
        const parser = new ReactiveResumeParser();
        const data = parser.convert(validationResult.result as ResumeData);

        await importResume({ data });
      }

      if (type === ImportType["reactive-resume-v3-json"]) {
        const parser = new ReactiveResumeV3Parser();
        const data = parser.convert(validationResult.result as ReactiveResumeV3);

        await importResume({ data });
      }

      if (type === ImportType["json-resume-json"]) {
        const parser = new JsonResumeParser();
        const data = parser.convert(validationResult.result as JsonResume);

        await importResume({ data });
      }

      if (type === ImportType["linkedin-data-export-zip"] || type === ImportType["linkedin-data-export-website"]) {
        const parser = new LinkedInParser();
        const data = parser.convert(validationResult.result as LinkedIn);
        await importResume({ data });
      }

      close();
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: importError?.message,
      });
    }
  };

  const onReset = () => {
    form.reset();
    setValidationResult(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <Form {...form}>
          <form className="space-y-4">
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center space-x-2.5">
                  <DownloadSimple />
                  <h2>{t`Import an existing resume`}</h2>
                </div>
              </DialogTitle>
              <DialogDescription>
                {`Upload a file from one of the accepted sources to parse existing data and import it into Coach LMS for easier editing.`}
              </DialogDescription>
            </DialogHeader>

            <FormField
              name="type"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Filetype`}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder={`Please select a file type`} />
                      </SelectTrigger>
                      <SelectContent>
                        {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
                        <SelectItem value="reactive-resume-json">
                          Coach LMS
                        </SelectItem>

                        {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
                        <SelectItem value="lpu-resume-json">
                          LPU Resume
                        </SelectItem>

                        {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
                        <SelectItem value="linkedin-data-export-zip">
                          LinkedIn Data Export (.zip)
                        </SelectItem>

                        {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
                        <SelectItem value="linkedin-data-export-website">
                          LinkedIn Data Import (profile)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

           {filetype && filetype !=='reactive-resume-json' &&
            filetype !== 'linkedin-data-export-website' &&
            filetype !== 'lpu-resume-json' && (
              <FormField
                name="file"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t`File`}</FormLabel>
                    <FormControl>
                      <Input
                        type="file"
                        key={accept}
                        accept={accept}
                        onChange={(event) => {
                          if (!event.target.files || !event.target.files.length) return;
                          field.onChange(event.target.files[0]);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                    {accept && (
                      <FormDescription>
                        {t({
                          message: `Accepts only ${accept} files`,
                          comment:
                            "Helper text to let the user know what filetypes are accepted. {accept} can be .pdf or .json.",
                        })}
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            )}
            {filetype && filetype!=='lpu-resume-json' && filetype !=='reactive-resume-json' && filetype === 'linkedin-data-export-website' && <FormField
              name="url"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{`Linkedin Profile Url :`}</FormLabel>
                  <FormControl>
                    <Input
                      disabled={validationResult?.isValid}
                      type="input"
                      key={accept}
                      accept={accept}
                      onChange={(event) => {
                        field.onChange(event.target.value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                  {accept && (
                    <FormDescription>
                      {t({
                        message: `Accepts only ${accept} files`,
                        comment:
                          "Helper text to let the user know what filetypes are accepted. {accept} can be .pdf or .json.",
                      })}
                    </FormDescription>
                  )}
                </FormItem>
              )}
            />}


            {validationResult?.isValid === false && validationResult.errors !== undefined && (
              <div className="space-y-2">
                <Label className="text-error">{t`Errors`}</Label>
                <ScrollArea orientation="vertical" className="h-[180px]">
                  <div className="whitespace-pre-wrap rounded bg-secondary-accent p-4 font-mono text-xs leading-relaxed">
                    {JSON.stringify(JSON.parse(validationResult.errors), null, 4)}
                  </div>
                </ScrollArea>
              </div>
            )}

            <DialogFooter>
              <AnimatePresence presenceAffectsLayout>
                {/* {filetype && (!validationResult ?? false) && ( */}
                  {filetype && validationResult === null && (
                  <Button type="button" disabled={lodr} onClick={onValidate}>
                    {(filetype=='reactive-resume-json' || filetype === 'linkedin-data-export-website' || filetype === 'lpu-resume-json') ? `Import`:`Validate`}
                  </Button>
                )}

                {validationResult !== null && !validationResult.isValid && (
                  <Button type="button" variant="secondary" onClick={onReset}>
                    {t`Discard`}
                  </Button>
                )}

                {validationResult !== null && validationResult.isValid && (
                  <>
                    <Button type="button" onClick={onImport} disabled={loading}>
                      {`Create`}
                    </Button>

                    <Button disabled type="button" variant="success">
                      <Check size={16} weight="bold" className="mr-2" />
                      {t`Validated`}
                    </Button>
                  </>
                )}
              </AnimatePresence>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
